"use client";

import { useRouter, usePathname } from "next/navigation";
import Link from "next/link";
import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useSpring, useMotionValueEvent } from "framer-motion";
import * as THREE from "three";
import { 
  Camera, Scale, Users, Globe2, HeartHandshake, Network, 
  ShieldCheck, Sparkles, MapPin, ArrowRight, Leaf, Clock, UserCheck
} from "lucide-react";
import { useLocale } from "@/i18n/context";

// ===================================================
// Ultra-Optimized & Highly Detailed 3D Nature Engine
// ===================================================
function Nature3DCanvas({ scrollProgress }: { scrollProgress: number }) {
  const containerRef = useRef<HTMLDivElement>(null);
  
  const sceneRef = useRef<THREE.Scene | null>(null);
  const fogRef = useRef<THREE.FogExp2 | null>(null);
  const ambientLightRef = useRef<THREE.AmbientLight | null>(null);
  const dirLightRef = useRef<THREE.DirectionalLight | null>(null);
  const groundMaterialRef = useRef<THREE.MeshStandardMaterial | null>(null);
  const trashGroupRef = useRef<THREE.Group | null>(null);
  const treesGroupRef = useRef<THREE.Group | null>(null);
  const flowersGroupRef = useRef<THREE.Group | null>(null);
  const animalsGroupRef = useRef<THREE.Group | null>(null);
  const birdsGroupRef = useRef<THREE.Group | null>(null);
  const riverMeshRef = useRef<THREE.Mesh | null>(null);
  
  // Particles
  const leavesInstancedRef = useRef<THREE.InstancedMesh | null>(null);
  const leavesDataRef = useRef<{ x: number, y: number, z: number, rx: number, ry: number, rz: number, speed: number, sway: number }[]>([]);
  const ambientSporesRef = useRef<THREE.Points | null>(null);

  const getTerrainHeight = (wx: number, wz: number) => {
    const py = -wz;
    return -3 + (Math.sin(wx * 0.05) * Math.cos(py * 0.06) * 3 + Math.sin(wx * 0.1) * 0.5);
  };

  useEffect(() => {
    if (!containerRef.current) return;

    const isMobile = window.innerWidth < 768;
    
    const scene = new THREE.Scene();
    sceneRef.current = scene;
    
    // 1. Atmosphere: Dusty Smog
    const initialFogColor = new THREE.Color("#e0e0e0"); 
    const fog = new THREE.FogExp2(initialFogColor, 0.015);
    scene.fog = fog;
    fogRef.current = fog;

    // Dynamic Camera FOV for Mobile
    const camera = new THREE.PerspectiveCamera(isMobile ? 65 : 50, window.innerWidth / window.innerHeight, 0.1, 1000);
    camera.position.set(0, isMobile ? 16 : 12, isMobile ? 55 : 45);
    camera.lookAt(0, 1, 0);

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: !isMobile, powerPreference: "high-performance" });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(isMobile ? 1 : Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = !isMobile; 
    if (!isMobile) renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    containerRef.current.appendChild(renderer.domElement);

    // 2. Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.7); 
    scene.add(ambientLight);
    ambientLightRef.current = ambientLight;

    const dirLight = new THREE.DirectionalLight(0xffcc80, 1.2); 
    dirLight.position.set(25, 45, 15);
    if (!isMobile) {
      dirLight.castShadow = true;
      dirLight.shadow.mapSize.width = 1024;
      dirLight.shadow.mapSize.height = 1024;
      dirLight.shadow.camera.near = 0.5;
      dirLight.shadow.camera.far = 100;
      dirLight.shadow.camera.left = -40;
      dirLight.shadow.camera.right = 40;
      dirLight.shadow.camera.top = 40;
      dirLight.shadow.camera.bottom = -40;
    }
    scene.add(dirLight);
    dirLightRef.current = dirLight;

    // 3. Terrain
    const groundGeo = new THREE.PlaneGeometry(160, 160, isMobile ? 32 : 64, isMobile ? 32 : 64);
    const pos = groundGeo.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const y = pos.getY(i);
      const z = Math.sin(x * 0.05) * Math.cos(y * 0.06) * 3 + Math.sin(x * 0.1) * 0.5;
      pos.setZ(i, z);
    }
    groundGeo.computeVertexNormals();

    const groundMat = new THREE.MeshStandardMaterial({
      color: new THREE.Color("#cfd8dc"),
      roughness: 1.0,
      flatShading: true,
    });
    groundMaterialRef.current = groundMat;

    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -3;
    if (!isMobile) ground.receiveShadow = true;
    scene.add(ground);

    // 4. River (Optimized Material)
    const riverGeo = new THREE.PlaneGeometry(24, 160, isMobile ? 1 : 32, isMobile ? 1 : 32); // Reduced segments on mobile
    const riverMat = new THREE.MeshStandardMaterial({ // Changed to StandardMaterial for mobile perf
      color: 0x90a4ae, 
      roughness: 0.2,
      metalness: 0.1,
      transparent: true,
      opacity: 0.8
    });
    const river = new THREE.Mesh(riverGeo, riverMat);
    river.rotation.x = -Math.PI / 2;
    river.rotation.z = 0.35;
    river.position.set(4, -2.5, 0);
    riverMeshRef.current = river;
    scene.add(river);

    // 5. Trash Group (Reduced Mobile Count)
    const trashCount = isMobile ? 30 : 120;
    const trashGroup = new THREE.Group();
    trashGroupRef.current = trashGroup;
    for (let i = 0; i < trashCount; i++) {
      const tx = (Math.random() - 0.5) * 120;
      const tz = (Math.random() - 0.5) * 100;
      if (Math.abs(tx) > 6 || Math.abs(tz) > 15) {
        const type = i % 3;
        let mesh;
        if (type === 0) mesh = new THREE.Mesh(new THREE.TorusGeometry(0.5, 0.2, 6, 12), new THREE.MeshStandardMaterial({ color: 0x424242 }));
        else if (type === 1) mesh = new THREE.Mesh(new THREE.CylinderGeometry(0.4, 0.4, 1.0, 8), new THREE.MeshStandardMaterial({ color: 0x8d6e63, metalness: 0.6 }));
        else mesh = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.4, 0.6), new THREE.MeshStandardMaterial({ color: 0x757575 }));
        
        mesh.position.set(tx, getTerrainHeight(tx, tz) + 0.3, tz);
        mesh.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, 0);
        if (!isMobile) mesh.castShadow = true;
        trashGroup.add(mesh);
      }
    }
    scene.add(trashGroup);

    // 6. Natural Trees (Reduced Mobile Count)
    const treesGroup = new THREE.Group();
    treesGroupRef.current = treesGroup;
    const treeCount = isMobile ? 15 : 50; // Reduced for mobile
    
    const createPine = (x: number, z: number) => {
      const tree = new THREE.Group();
      const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.5, 2.5, 5), new THREE.MeshStandardMaterial({ color: 0x4e342e }));
      trunk.position.y = 1.25;
      if (!isMobile) trunk.castShadow = true;
      tree.add(trunk);

      const foliageMat = new THREE.MeshStandardMaterial({ color: 0x14532d, flatShading: true });
      [3.0, 4.8, 6.2].forEach((yPos, idx) => {
        const cone = new THREE.Mesh(new THREE.ConeGeometry(2.2 - idx * 0.4, 2.8, 6), foliageMat);
        cone.position.y = yPos;
        if (!isMobile) cone.castShadow = true;
        tree.add(cone);
      });
      
      tree.position.set(x, getTerrainHeight(x, z), z);
      tree.scale.set(0.001, 0.001, 0.001); 
      return tree;
    };

    for (let i = 0; i < treeCount; i++) {
      const tx = (Math.random() - 0.5) * 130;
      const tz = (Math.random() - 0.5) * 110;
      if (Math.abs(tx) > 12) treesGroup.add(createPine(tx, tz));
    }
    scene.add(treesGroup);

    // 7. Colorful Flowers (Reduced Mobile Count)
    const flowersGroup = new THREE.Group();
    flowersGroupRef.current = flowersGroup;
    const flowerCount = isMobile ? 20 : 100; // Reduced for mobile
    const flowerColors = [0xf43f5e, 0xa855f7, 0xfbbf24, 0x0ea5e9, 0xffffff, 0xff69b4];
    
    for (let f = 0; f < flowerCount; f++) {
      const flower = new THREE.Group();
      const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.6), new THREE.MeshStandardMaterial({ color: 0x22c55e }));
      stem.position.y = 0.3;
      flower.add(stem);
      
      const petalMat = new THREE.MeshStandardMaterial({ color: flowerColors[f % flowerColors.length], flatShading: true });
      for (let p = 0; p < 4; p++) {
        const petal = new THREE.Mesh(new THREE.ConeGeometry(0.1, 0.3, 4), petalMat);
        const angle = (p / 4) * Math.PI * 2;
        petal.position.set(Math.sin(angle)*0.1, 0.6, Math.cos(angle)*0.1);
        petal.rotation.x = Math.PI/2;
        petal.rotation.z = angle;
        flower.add(petal);
      }
      
      const tx = (Math.random() - 0.5) * 90;
      const tz = (Math.random() - 0.5) * 90;
      flower.position.set(tx, getTerrainHeight(tx, tz), tz);
      flower.scale.set(0.001, 0.001, 0.001);
      flowersGroup.add(flower);
    }
    scene.add(flowersGroup);

    // 8. Animals
    const animalsGroup = new THREE.Group();
    animalsGroupRef.current = animalsGroup;
    
    const matDeer = new THREE.MeshStandardMaterial({ color: 0x8b5a2b, flatShading: true });
    const deer = new THREE.Mesh(new THREE.BoxGeometry(1.2, 2.5, 2.0), matDeer); 
    deer.userData = { startX: -60, endX: -16, z: -5 }; 
    deer.position.set(deer.userData.startX, getTerrainHeight(deer.userData.startX, deer.userData.z), deer.userData.z);
    if (!isMobile) deer.castShadow = true;
    animalsGroup.add(deer);

    const fox = new THREE.Mesh(new THREE.BoxGeometry(0.8, 1.2, 1.5), new THREE.MeshStandardMaterial({ color: 0xd97726 }));
    fox.userData = { startX: 60, endX: 18, z: -2 };
    fox.position.set(fox.userData.startX, getTerrainHeight(fox.userData.startX, fox.userData.z), fox.userData.z);
    animalsGroup.add(fox);

    scene.add(animalsGroup);

    // 9. Birds swooping down
    const birdsGroup = new THREE.Group();
    birdsGroupRef.current = birdsGroup;
    birdsGroup.position.y = 80; 
    
    for (let b = 0; b < 12; b++) {
      const bird = new THREE.Mesh(new THREE.ConeGeometry(0.2, 0.6, 4), new THREE.MeshStandardMaterial({ color: 0x3b82f6 }));
      bird.rotation.x = Math.PI/2;
      bird.position.set((Math.random() - 0.5) * 50, Math.random() * 15, (Math.random() - 0.5) * 50);
      birdsGroup.add(bird);
    }
    scene.add(birdsGroup);

    // 10. Natural Falling Leaves
    const leafCount = isMobile ? 50 : 200; // Reduced for mobile
    const leafGeo = new THREE.PlaneGeometry(0.25, 0.25);
    const leafMat = new THREE.MeshBasicMaterial({ color: 0xfacc15, side: THREE.DoubleSide, transparent: true, opacity: 0.9 });
    const leaves = new THREE.InstancedMesh(leafGeo, leafMat, leafCount);
    leavesInstancedRef.current = leaves;
    leaves.scale.setScalar(0);
    
    for (let i = 0; i < leafCount; i++) {
      leavesDataRef.current.push({
        x: (Math.random() - 0.5) * 80,
        y: Math.random() * 40,
        z: (Math.random() - 0.5) * 60,
        rx: Math.random() * Math.PI,
        ry: Math.random() * Math.PI,
        rz: Math.random() * Math.PI,
        speed: 0.015 + Math.random() * 0.02,
        sway: Math.random() * Math.PI * 2
      });
    }
    scene.add(leaves);

    // 11. Volumetric Glowing Spores (Dust)
    const sporeCount = isMobile ? 100 : 300;
    const sporeGeo = new THREE.BufferGeometry();
    const sporePositions = new Float32Array(sporeCount * 3);
    for(let i=0; i<sporeCount*3; i++) {
      sporePositions[i] = (Math.random() - 0.5) * 100;
    }
    sporeGeo.setAttribute('position', new THREE.BufferAttribute(sporePositions, 3));
    const sporeMat = new THREE.PointsMaterial({
      color: 0x757575,
      size: 0.15,
      transparent: true,
      opacity: 0.6,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    });
    const ambientSpores = new THREE.Points(sporeGeo, sporeMat);
    ambientSporesRef.current = ambientSpores;
    scene.add(ambientSpores);

    const handleResize = () => {
      const isMob = window.innerWidth < 768;
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.fov = isMob ? 65 : 50;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener("resize", handleResize);

    const dummy = new THREE.Object3D();
    let animationId: number;
    let isActive = true;
    const handleVisibilityChange = () => { isActive = !document.hidden; };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    const animate = () => {
      animationId = requestAnimationFrame(animate);
      if (!isActive) return;
      const time = Date.now() * 0.001;

      scene.rotation.y = Math.sin(time * 0.03) * 0.02; 

      // River Animation - Skip on mobile for massive performance gain
      if (riverMeshRef.current && !isMobile) {
        const rPos = riverMeshRef.current.geometry.attributes.position;
        for (let i = 0; i < rPos.count; i++) {
          const x = rPos.getX(i);
          const y = rPos.getY(i);
          const wave = Math.sin(x * 0.4 + time) * 0.2 + Math.cos(y * 0.2 + time) * 0.15;
          rPos.setZ(i, wave);
        }
        rPos.needsUpdate = true;
      }

      if (birdsGroupRef.current) {
        birdsGroupRef.current.children.forEach((bird, i) => {
          bird.position.x += Math.sin(time + i) * 0.05;
          bird.position.z += Math.cos(time + i) * 0.05;
          bird.rotation.y = -Math.atan2(Math.cos(time + i), Math.sin(time + i)); 
        });
      }

      if (leavesInstancedRef.current && leavesInstancedRef.current.scale.x > 0) {
        for (let i = 0; i < leafCount; i++) {
          const data = leavesDataRef.current[i];
          data.y -= data.speed;
          data.x += Math.sin(time * 2 + data.sway) * 0.02;
          data.rx += 0.01;
          data.ry += 0.02;
          
          if (data.y < -2) data.y = 40;

          dummy.position.set(data.x, data.y, data.z);
          dummy.rotation.set(data.rx, data.ry, data.rz);
          dummy.updateMatrix();
          leavesInstancedRef.current.setMatrixAt(i, dummy.matrix);
        }
        leavesInstancedRef.current.instanceMatrix.needsUpdate = true;
      }

      if (ambientSporesRef.current) {
        const positions = ambientSporesRef.current.geometry.attributes.position.array as Float32Array;
        for(let i=0; i<sporeCount; i++) {
          positions[i*3+1] += Math.sin(time + i) * 0.02;
          if (positions[i*3+1] > 50) positions[i*3+1] = -10;
        }
        ambientSporesRef.current.geometry.attributes.position.needsUpdate = true;
      }

      renderer.render(scene, camera);
    };
    animate();

    return () => {
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      cancelAnimationFrame(animationId);
      renderer.dispose();
      if (containerRef.current) containerRef.current.innerHTML = "";
    };
  }, []);

  // =======================================================
  // Cinematic Transition Logic
  // =======================================================
  useEffect(() => {
    if (fogRef.current && dirLightRef.current && groundMaterialRef.current && riverMeshRef.current && ambientSporesRef.current) {
      const envProgress = Math.min(1, scrollProgress * 2.5); 
      
      fogRef.current.color.lerpColors(new THREE.Color("#e0e0e0"), new THREE.Color("#f5f0e8"), envProgress);
      dirLightRef.current.color.lerpColors(new THREE.Color("#ffcc80"), new THREE.Color("#fff5ea"), envProgress);
      
      groundMaterialRef.current.color.lerpColors(new THREE.Color("#cfd8dc"), new THREE.Color("#206a4f"), envProgress);
      (riverMeshRef.current.material as THREE.MeshStandardMaterial).color.lerpColors(new THREE.Color("#90a4ae"), new THREE.Color("#0ea5e9"), envProgress);
      
      (ambientSporesRef.current.material as THREE.PointsMaterial).color.lerpColors(new THREE.Color("#757575"), new THREE.Color("#4ade80"), envProgress);
    }

    if (trashGroupRef.current) {
      const trashScale = Math.max(0, 1 - scrollProgress * 4);
      trashGroupRef.current.scale.setScalar(trashScale);
    }

    if (treesGroupRef.current) {
      treesGroupRef.current.children.forEach((tree, idx) => {
        const delay = (idx / treesGroupRef.current!.children.length) * 0.25;
        const p = Math.max(0, Math.min(1, (scrollProgress - 0.15 - delay) * 3));
        const scaleY = p;
        const scaleXZ = Math.max(0, p - 0.2) * 1.25; 
        tree.scale.set(scaleXZ, scaleY, scaleXZ);
      });
    }

    if (flowersGroupRef.current) {
      flowersGroupRef.current.children.forEach((flower, idx) => {
        const delay = (idx / flowersGroupRef.current!.children.length) * 0.2;
        const scale = Math.max(0, Math.min(1, (scrollProgress - 0.3 - delay) * 4));
        flower.scale.setScalar(scale);
      });
    }

    if (animalsGroupRef.current) {
      const animP = Math.max(0, Math.min(1, (scrollProgress - 0.5) * 2.5));
      const easeOut = 1 - Math.pow(1 - animP, 3);
      
      const deer = animalsGroupRef.current.children[0];
      const fox = animalsGroupRef.current.children[1];
      
      deer.position.x = deer.userData.startX + ((deer.userData.endX - deer.userData.startX) * easeOut);
      deer.position.y = getTerrainHeight(deer.position.x, deer.userData.z); 
      
      fox.position.x = fox.userData.startX + ((fox.userData.endX - fox.userData.startX) * easeOut);
      fox.position.y = getTerrainHeight(fox.position.x, fox.userData.z);
    }

    if (birdsGroupRef.current) {
      const animP = Math.max(0, Math.min(1, (scrollProgress - 0.4) * 2));
      const easeOut = 1 - Math.pow(1 - animP, 3);
      birdsGroupRef.current.position.y = 80 - (80 * easeOut); 
    }
    
    if (leavesInstancedRef.current) {
      const leafScale = Math.max(0, Math.min(1, (scrollProgress - 0.6) * 3));
      leavesInstancedRef.current.scale.setScalar(leafScale);
    }

  }, [scrollProgress]);

  return <div ref={containerRef} className="fixed inset-0 pointer-events-none z-0" />;
}

// ===================================================
// Main Hero Section Component 
// ===================================================
export function HeroSection() {
  const { scrollYProgress } = useScroll();
  const smoothProgress = useSpring(scrollYProgress, { 
    mass: 1.5,       
    stiffness: 25,   
    damping: 35,     
    restDelta: 0.0001 
  });
  
  const [scrollVal, setScrollVal] = useState(0);
  const router = useRouter();
  
  const pathname = usePathname();
  const [animationKey, setAnimationKey] = useState("initial");

  const [heroName, setHeroName] = useState("");
  const [heroCity, setHeroCity] = useState("");

  const handleInitialSubmit = () => {
    if (!heroName || !heroCity) return; 
    const params = new URLSearchParams({ name: heroName, city: heroCity });
    router.push(`/volunteer-form?${params.toString()}`);
  };

  useMotionValueEvent(smoothProgress, "change", (latest) => {
    setScrollVal(latest);
  });


  const { t, isRtl } = useLocale();
  const heroTitleText = t.hero.title || "Green Campaign: From an Idea to a Regional Movement for a Clean Earth";
  const [typedTitle, setTypedTitle] = useState("");

  useEffect(() => {
    setTimeout(() => setTypedTitle(""), 0);
    let i = 0;
    const timer = setInterval(() => {
      i++;
      if (i <= heroTitleText.length) {
        setTypedTitle(heroTitleText.slice(0, i));
      } else {
        clearInterval(timer);
      }
    }, 35); 
    return () => clearInterval(timer);
  }, [heroTitleText, pathname]);

  return (
    <div className={`relative min-h-screen bg-transparent text-[#206A4F] dark:text-emerald-400 overflow-hidden ${isRtl ? 'dir-rtl' : 'dir-ltr'}`}>
      <Nature3DCanvas scrollProgress={scrollVal} />
      
      {/* Top Progress Bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[#206A4F] to-[#4ade80] z-50 origin-left rtl:origin-right"
        style={{ scaleX: smoothProgress }}
      />
      
      {/* Responsive padding and spacing fixes */}
<div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 space-y-16 md:space-y-24 lg:pb-24 lg:space-y-32">        
        
        {/* === Hero Intro === */}
        <section className="min-h-[80vh] md:min-h-[90vh] flex flex-col justify-center items-start pt-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.1 }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-4xl w-full min-h-[350px] sm:min-h-[400px]"
          >

            <div className="inline-flex items-center gap-2 px-4 py-2 sm:px-5 sm:py-2.5 rounded-full bg-white/80 dark:bg-black/40 border border-white/60 dark:border-emerald-500/30 text-[#206A4F] dark:text-emerald-400 font-bold text-xs sm:text-sm mb-6 md:mb-8 shadow-sm md:backdrop-blur-xl">
              <Leaf size={16} className="text-[#206A4F] dark:text-emerald-400 animate-pulse" />
              <span>{t.hero.badge}</span>
            </div>
            
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.3] text-[#206A4F] dark:text-white mb-6 md:mb-8 min-h-[2.5em] md:min-h-[3em]">
              {typedTitle}
              <motion.span
                animate={{ opacity: [0, 1, 0] }}
                transition={{ repeat: Infinity, duration: 0.9 }}
                className="inline-block ms-1 w-[6px] min-w-[6px] shrink-0 h-[0.85em] bg-[#206A4F] dark:bg-emerald-400 align-middle"
              />
            </h1>
            
            <p className="text-base sm:text-lg lg:text-xl text-[#206A4F]/90 dark:text-zinc-200 leading-relaxed font-medium max-w-3xl bg-[#F5F0E8]/80 dark:bg-zinc-900/80 backdrop-blur-md p-4 sm:p-6 rounded-2xl md:rounded-3xl border border-white/30 dark:border-zinc-800 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              {t.hero.subtitle}
            </p>
            
            <div className="mt-8 md:mt-12 flex flex-wrap gap-3 sm:gap-5">
              <Link
                href="#join"
                className="inline-flex items-center gap-3 bg-[#206A4F] text-[#F5F0E8] dark:bg-emerald-500 dark:text-black px-6 py-3 sm:px-8 sm:py-4 rounded-2xl font-black shadow-xl hover:bg-[#18523d] dark:hover:bg-emerald-400 hover:-translate-y-1 transition-all duration-300 text-sm sm:text-base"
              >
                <span>{t.hero.ctaPrimary}</span>
                <ArrowRight size={20} className="rtl:rotate-180" />
              </Link>
              <Link
                href="/articles"
                className="inline-flex items-center gap-2 bg-white/80 dark:bg-zinc-800/80 backdrop-blur-xl border border-[#206A4F]/20 dark:border-zinc-700 text-[#206A4F] dark:text-emerald-300 px-6 py-3 sm:px-8 sm:py-4 rounded-2xl font-bold hover:bg-white/70 dark:hover:bg-zinc-800 transition-all duration-300 hover:-translate-y-1 text-sm sm:text-base"
              >
                <span>{t.hero.ctaSecondary}</span>
              </Link>
            </div>
          </motion.div>
        </section>

        {/* === Statistics Glassmorphism === */}
        <section className="relative z-20">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 bg-white/80 dark:bg-zinc-900/80 backdrop-blur-2xl p-6 md:p-10 rounded-3xl md:rounded-[2rem] border border-white/60 dark:border-zinc-800 shadow-[0_8px_40px_rgb(32,106,79,0.1)] dark:shadow-none mx-auto max-w-6xl transition-all duration-300"
          >
            {[
              { icon: Users, val: "10,000+", label: t.hero.stats.participation, sub: t.hero.stats.participationSub },
              { icon: Clock, val: "50,000", label: t.hero.stats.hours, sub: t.hero.stats.hoursSub },
              { icon: UserCheck, val: "2,500", label: t.hero.stats.volunteers, sub: t.hero.stats.volunteersSub }
            ].map((stat, i) => (
              <div key={i} className="flex flex-col items-center text-center p-4 relative group">
                <div className="absolute inset-0 bg-white/0 group-hover:bg-white/20 dark:group-hover:bg-zinc-800/50 transition-colors rounded-3xl -z-10" />
                <div className="w-14 h-14 md:w-16 md:h-16 bg-[#206A4F]/10 dark:bg-emerald-500/20 rounded-2xl flex items-center justify-center mb-4 md:mb-6 border border-[#206A4F]/20 dark:border-emerald-500/30 group-hover:scale-110 transition-transform duration-500">
                  <stat.icon size={28} className="text-[#206A4F] dark:text-emerald-400 animate-pulse md:hidden" />
                  <stat.icon size={32} className="text-[#206A4F] dark:text-emerald-400 animate-pulse hidden md:block" />
                </div>
                <div className="text-3xl md:text-4xl lg:text-5xl font-black text-[#206A4F] dark:text-white mb-2 tracking-tight">{stat.val}</div>
                <p className="text-base md:text-lg font-bold text-[#206A4F]/90 dark:text-emerald-300">{stat.label}</p>
                <p className="text-xs md:text-sm text-[#206A4F]/60 dark:text-zinc-400 mt-1">{stat.sub}</p>
              </div>
            ))}
          </motion.div>
        </section>

        {/* === Birth of Idea === */}
        <section className="scroll-mt-32">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }}
            transition={{ duration: 1 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center bg-white/80 dark:bg-zinc-900/80 backdrop-blur-2xl p-6 md:p-10 lg:p-16 rounded-3xl md:rounded-[2.5rem] border border-white/60 dark:border-zinc-800 shadow-xl transition-all duration-300"
          >
            <div>
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-[#206A4F] dark:bg-emerald-500 text-[#F5F0E8] dark:text-black flex items-center justify-center mb-6 md:mb-8 shadow-lg">
                <Sparkles size={28} className="md:hidden" />
                <Sparkles size={32} className="hidden md:block" />
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold mb-4 md:mb-6 text-[#206A4F] dark:text-white">
                {t.hero.aboutTitle}
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-[#206A4F]/90 dark:text-zinc-200 leading-relaxed mb-4 md:mb-6 font-medium">
                {t.hero.aboutQuote}
              </p>
              <p className="text-sm sm:text-base md:text-lg text-[#206A4F]/80 dark:text-zinc-400 leading-relaxed font-medium">
                {t.hero.aboutDesc}
              </p>
            </div>
            <div className="relative flex justify-center h-full mt-8 md:mt-0">
              <div className="w-full h-64 md:h-80 rounded-3xl md:rounded-[2rem] bg-gradient-to-br from-white/60 dark:from-zinc-800/80 to-[#206A4F]/10 dark:to-emerald-950/40 border border-white/50 dark:border-zinc-700 flex flex-col items-center justify-center p-6 md:p-8 text-center space-y-4 md:space-y-6 shadow-inner relative overflow-hidden group">
                <div className="absolute inset-0 bg-[#206A4F]/5 dark:bg-emerald-500/10 translate-y-full group-hover:translate-y-0 transition-transform duration-700 ease-out" />
                <Globe2 size={64} className="text-[#206A4F] dark:text-emerald-400 animate-spin-slow relative z-10 md:hidden" />
                <Globe2 size={80} className="text-[#206A4F] dark:text-emerald-400 animate-spin-slow relative z-10 hidden md:block" />
                <span className="font-black text-xl md:text-2xl text-[#206A4F] dark:text-white relative z-10">
                  {t.hero.impactTitle}
                </span>
                <p className="text-sm md:text-base text-[#206A4F]/80 dark:text-zinc-300 font-medium relative z-10">
                  {t.hero.impactDesc}
                </p>
              </div>
            </div>
          </motion.div>
        </section>

        {/* === Core Values === */}
        <section className="space-y-10 md:space-y-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: false }}
            className="text-center max-w-3xl mx-auto bg-white/80 dark:bg-zinc-900/80 backdrop-blur-xl p-6 md:p-8 rounded-3xl md:rounded-[2rem] border border-white/50 dark:border-zinc-800 shadow-lg transition-all duration-300"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold mb-4 text-[#206A4F] dark:text-white">
              {t.hero.valuesTitle}
            </h2>
            <p className="text-[#206A4F]/80 dark:text-zinc-300 text-base sm:text-lg md:text-xl font-medium">
              {t.hero.valuesDesc}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {[
              { icon: <Camera size={28} className="md:hidden" />, iconLg: <Camera size={32} className="hidden md:block" />, title: t.hero.pillar1Title, desc: t.hero.pillar1Desc },
              { icon: <Scale size={28} className="md:hidden" />, iconLg: <Scale size={32} className="hidden md:block" />, title: t.hero.pillar2Title, desc: t.hero.pillar2Desc },
              { icon: <Users size={28} className="md:hidden" />, iconLg: <Users size={32} className="hidden md:block" />, title: t.hero.pillar3Title, desc: t.hero.pillar3Desc },
            ].map((pillar, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false }}
                transition={{ delay: idx * 0.2, duration: 0.8 }}
                whileHover={{ y: -10 }}
                className="bg-white/80 dark:bg-zinc-900/80 backdrop-blur-2xl p-6 md:p-10 rounded-3xl md:rounded-[2rem] border border-white/60 dark:border-zinc-800 shadow-[0_8px_30px_rgb(0,0,0,0.06)] dark:shadow-none flex flex-col items-start transition-all duration-300"
              >
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-[#206A4F] dark:bg-emerald-500 text-[#F5F0E8] dark:text-black flex items-center justify-center mb-6 md:mb-8 shadow-lg">
                  {pillar.icon}
                  {pillar.iconLg}
                </div>
                <h3 className="text-xl md:text-2xl font-black mb-3 md:mb-4 text-[#206A4F] dark:text-white">{pillar.title}</h3>
                <p className="text-[#206A4F]/80 dark:text-zinc-300 leading-relaxed text-sm sm:text-base md:text-lg font-medium">{pillar.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* === Active Branches === */}
        <section id="branches" className="scroll-mt-32">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }}
            className="bg-[#206A4F]/95 dark:bg-zinc-900 backdrop-blur-2xl text-[#F5F0E8] dark:text-zinc-100 rounded-3xl md:rounded-[3rem] p-6 md:p-10 lg:p-16 shadow-2xl relative overflow-hidden border border-[#206A4F]/20 dark:border-zinc-800 transition-all duration-300"
          >
            <div className="absolute top-0 right-0 w-[40rem] h-[40rem] bg-[#4ade80]/15 dark:bg-emerald-500/10 rounded-full blur-[120px] pointer-events-none" />
            
            <div className="relative z-10 max-w-4xl">
              <span className="inline-block px-4 py-2 md:px-5 rounded-full bg-white/10 dark:bg-emerald-500/20 text-white dark:text-emerald-300 text-xs md:text-sm font-bold mb-4 md:mb-6 border border-white/20 dark:border-emerald-500/30 backdrop-blur-md shadow-sm">
                {t.hero.branchesBadge}
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-5xl font-black mb-4 md:mb-8 leading-tight text-white">
                {t.hero.branchesTitle}
              </h2>
              <p className="text-white/85 dark:text-zinc-300 text-base md:text-lg lg:text-xl mb-8 md:mb-12 leading-relaxed font-medium">
                {t.hero.branchesDesc}
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                {[
                  { name: t.hero.chapterMainName, role: t.hero.chapterMainRole },
                  { name: t.hero.chapterTehranName, role: t.hero.chapterRoleActive },
                  { name: t.hero.chapterKarajName, role: t.hero.chapterRoleActive },
                  { name: t.hero.chapterMashhadName, role: t.hero.chapterRoleActive },
                  { name: t.hero.chapterTabrizName, role: t.hero.chapterRoleActive },
                  { name: t.hero.chapterIntName, role: t.hero.chapterIntRole },
                ].map((branch, i) => (
                  <motion.div
                    key={i}
                    whileHover={{ scale: 1.03, backgroundColor: "rgba(255,255,255,0.18)" }}
                    className="bg-white/10 dark:bg-zinc-800/60 backdrop-blur-xl p-4 md:p-5 rounded-2xl border border-white/15 dark:border-zinc-700 flex items-center gap-3 md:gap-4 cursor-default transition-colors duration-300 shadow-sm"
                  >
                    <div className="p-2 md:p-2.5 rounded-xl bg-white/15 dark:bg-emerald-500/20 shrink-0">
                      <MapPin size={20} className="text-[#4ade80] md:hidden" />
                      <MapPin size={24} className="text-[#4ade80] hidden md:block" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-extrabold text-sm md:text-base lg:text-lg text-white truncate">{branch.name}</h3>
                      <p className="text-xs md:text-sm text-white/70 dark:text-emerald-300/80 font-bold mt-0.5">{branch.role}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </section>

        {/* === Join Form === */}
        <section id="join" className="scroll-mt-32">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }}
            className="bg-white/80 dark:bg-zinc-900/80 backdrop-blur-2xl border border-white/60 dark:border-zinc-800 rounded-3xl md:rounded-[3rem] p-6 md:p-10 lg:p-16 text-center max-w-4xl mx-auto shadow-[0_20px_60px_rgb(32,106,79,0.08)] dark:shadow-none relative transition-all duration-300"
          >
            <div className="inline-block p-4 md:p-5 rounded-3xl md:rounded-[2rem] bg-[#206A4F] dark:bg-emerald-500 text-white dark:text-black mb-6 md:mb-8 shadow-xl">
              <Leaf size={36} className="animate-pulse md:hidden" />
              <Leaf size={48} className="animate-pulse hidden md:block" />
            </div>
            
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-black mb-4 md:mb-6 leading-tight text-[#206A4F] dark:text-white">
              {t.hero.joinTitle}
            </h2>
            
            <p className="text-base md:text-lg lg:text-xl text-[#206A4F]/80 dark:text-zinc-300 font-medium max-w-2xl mx-auto mb-8 md:mb-12 leading-relaxed">
              {t.hero.joinDesc}
            </p>
            
            <form className="max-w-md mx-auto space-y-4 md:space-y-5 mb-6 md:mb-8">
              <input 
                type="text" 
                value={heroName}
                onChange={(e) => setHeroName(e.target.value)}
                placeholder={t.contactUs.nameLabel} 
                className="w-full px-4 md:px-6 py-3 md:py-4 rounded-2xl bg-white/80 dark:bg-zinc-800 backdrop-blur-sm border-2 border-[#206A4F]/15 dark:border-zinc-700 text-[#206A4F] dark:text-white font-bold focus:outline-none focus:border-[#206A4F] dark:focus:border-emerald-500 transition-all placeholder:font-medium placeholder:text-zinc-400 shadow-sm text-sm md:text-base" 
              />
              <input 
                type="text" 
                value={heroCity}
                onChange={(e) => setHeroCity(e.target.value)}
                placeholder={t.hero.joinCityPlaceholder} 
                className="w-full px-4 md:px-6 py-3 md:py-4 rounded-2xl bg-white/80 dark:bg-zinc-800 backdrop-blur-sm border-2 border-[#206A4F]/15 dark:border-zinc-700 text-[#206A4F] dark:text-white font-bold focus:outline-none focus:border-[#206A4F] dark:focus:border-emerald-500 transition-all placeholder:font-medium placeholder:text-zinc-400 shadow-sm text-sm md:text-base" 
              />
              <button 
                type="button" 
                onClick={handleInitialSubmit}
                className="w-full bg-[#206A4F] dark:bg-emerald-500 text-[#F5F0E8] dark:text-black py-3 md:py-4 rounded-2xl font-black text-lg md:text-xl hover:bg-[#17523d] dark:hover:bg-emerald-400 transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1 mt-2"
              >
                {t.hero.ctaPrimary}
              </button>
            </form>
            
            <p className="text-xs md:text-sm lg:text-base font-bold text-[#206A4F]/70 dark:text-zinc-400">
              {t.hero.joinFooterNotice}
            </p>
          </motion.div>
        </section>

      </div>
    </div>
  );
}